import React, { useEffect, useRef, useState } from 'react';
import Modal from 'react-modal';

import DropLoaderLarge from '@C/Utils/DropLoaderLarge';

import './SummaryModal.scoped.scss';

const LOADING_MESSAGES = [
  'Calculating estimated profits...',
  'Reviewing transactions...',
  'Tallying collections and shortages...',
  'Updating the summary...',
  'Almost there...',
];

const EXPENSE_LABELS = {
  vendor_payments: 'Vendor Payments',
  Salary: 'Salary',
  Other_expense: 'Other Expenses',
  salary_advance: 'Advance Salary',
  temp_amount_given: 'Temp. credit',
  cash_given: 'Credit cash given',
};

const INCOME_LABELS = {
  fuel_sale: 'Fuel Sales',
  lube_sale: 'Lube / Product sale',
  credit_collection: 'Credit Collection',
  other_incomes: 'Other Income',
  salary_advance_return: 'Advance salary return',
  temp_return: 'Temp credit return',
};

const SHORTAGE_LABELS = {
  collections: 'Total collected (Banks and Cash)',
  cashier_expenses: 'Cashier expenses',
  cash_received_from_office: 'Cash received from office',
};

const formatCurrency = (value) =>
  new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
  }).format(value || 0);

const buildRows = (source, labels) =>
  Object.keys(source || {}).map((key) => ({
    key,
    label: labels[key] || key,
    amount: source[key] || 0,
  }));

const SummaryLoader = () => {
  const [messageIndex, setMessageIndex] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setMessageIndex((prev) => (prev + 1) % LOADING_MESSAGES.length);
    }, 900);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="summary-loader">
      <div className="summary-loader__spinner">
        <DropLoaderLarge />
      </div>
      <p className="summary-loader__text">{LOADING_MESSAGES[messageIndex]}</p>
    </div>
  );
};

const SummaryModal = ({ isOpen, onClose, data }) => {
  const [showLoader, setShowLoader] = useState(true);
  const loadedDataRef = useRef(null);

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    if (loadedDataRef.current === data) {
      setShowLoader(false);
      return;
    }
    setShowLoader(true);
    const timer = setTimeout(() => {
      loadedDataRef.current = data;
      setShowLoader(false);
    }, 4500);
    return () => clearTimeout(timer);
  }, [isOpen, data]);

  const summary = data || {};
  const expenseRows = buildRows(summary.expense, EXPENSE_LABELS);
  const incomeRows = buildRows(summary.income, INCOME_LABELS);
  const shortageRows = buildRows(summary.shortage, SHORTAGE_LABELS);

  const totalExpense = expenseRows.reduce((sum, row) => sum + row.amount, 0);
  const totalIncome = incomeRows.reduce((sum, row) => sum + row.amount, 0);
  const estimatedProfit = totalIncome - totalExpense;

  const collections = summary.shortage?.collections || 0;
  const cashierExpenses = summary.shortage?.cashier_expenses || 0;
  const cashReceivedFromOffice = summary.shortage?.cash_received_from_office || 0;
  const estimatedShortage =
    (collections + cashierExpenses) - totalIncome;

  const rowCount = Math.max(expenseRows.length, incomeRows.length);

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onClose}
      className="sub-xl-modal-container summary-modal-container"
      overlayClassName="xl-modal-overlay"
      contentLabel="Business Summary"
    >
      <div className="modal-component">
        <section className="widget full-width summary-modal">
          <header className="summary-modal__header">
            <h3>Summary</h3>
            <button
              type="button"
              className="summary-modal__close"
              onClick={onClose}
            >
              &times;
            </button>
          </header>
          <main className="summary-modal__content">
            {showLoader ? (
              <SummaryLoader />
            ) : (
              <div className="summary-table-wrapper">
                <table className="dsr" data-name="Business Summary">
                  <thead>
                    <tr data-header={true}>
                      <th colSpan={2}>Expenses</th>
                      <th colSpan={2}>Income</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Array.from({ length: rowCount }).map((_, i) => (
                      <tr key={i}>
                        <td className="label-cell">
                          {expenseRows[i]?.label || ''}
                        </td>
                        <td>
                          {expenseRows[i]
                            ? formatCurrency(expenseRows[i].amount)
                            : ''}
                        </td>
                        <td className="label-cell">
                          {incomeRows[i]?.label || ''}
                        </td>
                        <td>
                          {incomeRows[i]
                            ? formatCurrency(incomeRows[i].amount)
                            : ''}
                        </td>
                      </tr>
                    ))}
                    <tr className="summary-row">
                      <td className="label-cell">Total</td>
                      <td>{formatCurrency(totalExpense)}</td>
                      <td className="label-cell">Total</td>
                      <td>{formatCurrency(totalIncome)}</td>
                    </tr>
                    <tr className="summary-row estimated-row">
                      <td colSpan={3} className="label-cell">
                        Estimated Profit
                      </td>
                      <td className={estimatedProfit >= 0 ? 'green' : 'red'}>
                        {formatCurrency(estimatedProfit)}
                      </td>
                    </tr>
                  </tbody>
                </table>

                <table className="dsr" data-name="Collection Summary">
                  <tbody>
                    {shortageRows.map((row) => (
                      <tr key={row.key}>
                        <td className="label-cell" dangerouslySetInnerHTML={{ __html: `${row.label} ${row.label === 'Cashier expenses' ? '<sup style="color: red;">*</sup>' : ''}` }}></td>
                        <td>{formatCurrency(row.amount)}</td>
                      </tr>
                    ))}
                    <tr className="summary-row estimated-row">
                      <td className="label-cell">
                        Estimated Shortage<sup style={{ color: 'red' }}>**</sup>
                      </td>
                      <td className={estimatedShortage >= 0 ? 'green' : 'red'}>
                        {formatCurrency(estimatedShortage)}
                      </td>
                    </tr>
                    <tr>
                      <td colSpan={2} style={{ textAlign: 'left', backgroundColor: '#f7e5c5', fontSize: '11px', fontWeight: 600, lineHeight: '1.2em' }}>
                        * Cashier expenses include other expense, advance salary, credit sale cash given, temp credit given - from COD.<br />
                        **Includes Cashier shortage
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            )}
          </main>
        </section>
      </div>
    </Modal>
  );
};

export default SummaryModal;
